// Remote-control replay (ported from skuru) — runs in the CUSTOMER's tab
// while a remote support session is sharing. Receives the admin's input over
// the WebRTC data channel and replays it against the live DOM.
//
// Protocol (JSON over the "control" channel; coordinates normalized 0..1):
//   {t:"pointer", x, y}        — admin cursor → guide overlay
//   {t:"click"|"dblclick", x, y}
//   {t:"type", text}           — insert at the focused input
//   {t:"key", key}             — special keys
//   {t:"scroll", x, y, dx, dy}
//
// Synthetic events are untrusted: React handlers fire, native default
// actions don't — so text edits go through the native value setter +
// InputEvent so React's tracker notices.

interface PointerMsg {
  t: "pointer";
  x: number;
  y: number;
}
interface ClickMsg {
  t: "click" | "dblclick";
  x: number;
  y: number;
}
interface TypeMsg {
  t: "type";
  text: string;
}
interface KeyMsg {
  t: "key";
  key: string;
}
interface ScrollMsg {
  t: "scroll";
  x: number;
  y: number;
  dx: number;
  dy: number;
}
export type ControlMessage = PointerMsg | ClickMsg | TypeMsg | KeyMsg | ScrollMsg;

export function attachControlReplay(dc: RTCDataChannel): () => void {
  const cursor = buildCursorOverlay();
  document.body.appendChild(cursor);
  let hideTimer: number | null = null;

  dc.onmessage = (e: MessageEvent) => {
    let msg: ControlMessage;
    try {
      msg = JSON.parse(String(e.data)) as ControlMessage;
    } catch {
      return;
    }
    try {
      handle(msg);
    } catch {
      /* one bad event must never break the stream */
    }
  };

  function toViewport(nx: number, ny: number): { x: number; y: number } {
    return {
      x: Math.max(0, Math.min(1, nx)) * window.innerWidth,
      y: Math.max(0, Math.min(1, ny)) * window.innerHeight,
    };
  }

  function handle(msg: ControlMessage): void {
    switch (msg.t) {
      case "pointer": {
        const { x, y } = toViewport(msg.x, msg.y);
        cursor.style.display = "block";
        cursor.style.transform = `translate(${x}px, ${y}px)`;
        if (hideTimer !== null) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
          cursor.style.display = "none";
        }, 3000);
        break;
      }
      case "click":
      case "dblclick": {
        const { x, y } = toViewport(msg.x, msg.y);
        const el = document.elementFromPoint(x, y) as HTMLElement | null;
        if (!el) break;
        const opts: MouseEventInit = { bubbles: true, cancelable: true, clientX: x, clientY: y, view: window };
        el.dispatchEvent(new PointerEvent("pointerdown", opts));
        el.dispatchEvent(new MouseEvent("mousedown", opts));
        focusTarget(el);
        el.dispatchEvent(new PointerEvent("pointerup", opts));
        el.dispatchEvent(new MouseEvent("mouseup", opts));
        el.dispatchEvent(new MouseEvent("click", opts));
        if (msg.t === "dblclick") el.dispatchEvent(new MouseEvent("dblclick", opts));
        break;
      }
      case "type": {
        insertText(msg.text);
        break;
      }
      case "key": {
        replayKey(msg.key);
        break;
      }
      case "scroll": {
        const { x, y } = toViewport(msg.x, msg.y);
        scrollAt(document.elementFromPoint(x, y), msg.dx, msg.dy);
        break;
      }
    }
  }

  return () => {
    dc.onmessage = null;
    if (hideTimer !== null) window.clearTimeout(hideTimer);
    cursor.remove();
  };
}

function buildCursorOverlay(): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.style.cssText =
    "position:fixed;left:0;top:0;z-index:2147483647;pointer-events:none;display:none;will-change:transform;";
  wrap.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))">' +
    '<path d="M4 2 L20 12 L12.5 13.5 L9 21 Z" fill="#DF8048" stroke="#fff" stroke-width="1.5"/></svg>' +
    '<span style="margin-left:2px;background:#DF8048;color:#fff;font:600 10px system-ui;' +
    'padding:2px 6px;border-radius:9999px;white-space:nowrap;vertical-align:top;">Clevis support</span>';
  return wrap;
}

function focusTarget(el: HTMLElement): void {
  const focusable = el.closest(
    "input, textarea, select, button, a[href], [tabindex], [contenteditable]",
  ) as HTMLElement | null;
  (focusable ?? el).focus?.();
}

function editableActive(): HTMLInputElement | HTMLTextAreaElement | null {
  const el = document.activeElement;
  if (el instanceof HTMLTextAreaElement) return el;
  if (el instanceof HTMLInputElement) {
    const noText = ["checkbox", "radio", "button", "submit", "file", "range"];
    if (!noText.includes(el.type)) return el;
  }
  return null;
}

function setValue(el: HTMLInputElement | HTMLTextAreaElement, next: string, caret: number): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, next);
  else el.value = next;
  try {
    el.selectionStart = el.selectionEnd = caret;
  } catch {
    /* number/email inputs disallow selection APIs */
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function insertText(text: string): void {
  const el = editableActive();
  if (el) {
    let start: number, end: number;
    try {
      start = el.selectionStart ?? el.value.length;
      end = el.selectionEnd ?? start;
    } catch {
      start = end = el.value.length;
    }
    setValue(el, el.value.slice(0, start) + text + el.value.slice(end), start + text.length);
    return;
  }
  const ce = document.activeElement as HTMLElement | null;
  if (ce?.isContentEditable) {
    document.execCommand("insertText", false, text);
  }
}

function replayKey(key: string): void {
  const active = (document.activeElement as HTMLElement | null) ?? document.body;
  const opts: KeyboardEventInit = { key, bubbles: true, cancelable: true };
  active.dispatchEvent(new KeyboardEvent("keydown", opts));

  const el = editableActive();
  if (el && (key === "Backspace" || key === "Delete")) {
    let start: number, end: number;
    try {
      start = el.selectionStart ?? el.value.length;
      end = el.selectionEnd ?? start;
    } catch {
      start = end = el.value.length;
    }
    if (start === end) {
      if (key === "Backspace" && start > 0) start -= 1;
      if (key === "Delete" && end < el.value.length) end += 1;
    }
    if (start !== end) {
      setValue(el, el.value.slice(0, start) + el.value.slice(end), start);
    }
  }

  active.dispatchEvent(new KeyboardEvent("keyup", opts));
}

function scrollAt(start: Element | null, dx: number, dy: number): void {
  let el: Element | null = start;
  while (el && el !== document.body && el !== document.documentElement) {
    const s = getComputedStyle(el);
    const scrollableY = el.scrollHeight > el.clientHeight && (s.overflowY === "auto" || s.overflowY === "scroll");
    const scrollableX = el.scrollWidth > el.clientWidth && (s.overflowX === "auto" || s.overflowX === "scroll");
    if (scrollableY || scrollableX) {
      el.scrollBy(dx, dy);
      return;
    }
    el = el.parentElement;
  }
  window.scrollBy(dx, dy);
}
